import { alpha, createTheme } from "@mui/material/styles";
import type { PaletteMode } from "@mui/material";

export const MONO = '"IBM Plex Mono", ui-monospace, Menlo, monospace';

export function createAtlasTheme(mode: PaletteMode) {
  const dark = mode === "dark";
  const border = dark ? "#303a34" : "#dce3df";
  const paper = dark ? "#151d18" : "#fbfdfc";
  const background = dark ? "#0c1210" : "#eef3f1";
  const textPrimary = dark ? "#edf5f0" : "#202923";
  const textSecondary = dark ? "#a9b6ae" : "#66716b";
  const accent = dark ? "#63c4b8" : "#0f766e";
  const accentDark = dark ? "#8ad5cc" : "#09584f";
  const accentLight = dark ? "#3a998e" : "#5a9f96";
  const softShadow = dark
    ? "0 18px 48px rgba(1, 6, 3, 0.34)"
    : "0 18px 48px rgba(51, 69, 60, 0.09)";
  const surfaceHighlight = dark
    ? "rgba(225, 247, 235, 0.08)"
    : "rgba(255, 255, 255, 0.78)";
  const surfaceBorder = dark
    ? "rgba(214, 239, 225, 0.11)"
    : "rgba(255, 255, 255, 0.72)";

  return createTheme({
    palette: {
      mode,
      primary: {
        main: accent,
        dark: accentDark,
        light: accentLight,
        contrastText: dark ? "#0d1713" : "#f7fffc",
      },
      secondary: {
        main: dark ? "#9aa69f" : "#65716b",
        dark: dark ? "#c5cec8" : "#3f4a44",
        light: dark ? "#737f78" : "#929d97",
        contrastText: dark ? "#111713" : "#f9fbfa",
      },
      error: {
        main: dark ? "#ed858c" : "#bd454f",
        dark: dark ? "#ffb1b6" : "#93313a",
        light: dark ? "#bc5961" : "#d87980",
        contrastText: dark ? "#241012" : "#ffffff",
      },
      warning: {
        main: dark ? "#e4a85f" : "#a76010",
        dark: dark ? "#f4c789" : "#7c4708",
        light: dark ? "#b77e3b" : "#d18b3e",
        contrastText: dark ? "#211609" : "#ffffff",
      },
      info: {
        main: dark ? "#80aebb" : "#466b78",
        dark: dark ? "#afd0d8" : "#304f59",
        light: dark ? "#537f8c" : "#7b99a3",
        contrastText: dark ? "#0c171a" : "#ffffff",
      },
      success: {
        main: dark ? "#82bd8e" : "#3f7d4f",
        dark: dark ? "#aed3b5" : "#2d5f3a",
        light: dark ? "#568f62" : "#76a982",
        contrastText: dark ? "#0e190f" : "#ffffff",
      },
      background: { default: background, paper },
      text: { primary: textPrimary, secondary: textSecondary },
      divider: border,
      grey: dark
        ? {
            50: "#1b231e",
            100: "#222c26",
            200: "#2b3730",
            300: "#3a4740",
            700: "#a9b6ae",
            900: "#edf5f0",
          }
        : {
            50: "#f8faf8",
            100: "#f0f3f1",
            200: "#e6ebe8",
            300: "#d5dcd8",
            700: "#48534d",
            900: "#202923",
          },
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: '"IBM Plex Sans", "Helvetica Neue", Arial, sans-serif',
      fontSize: 15,
      h1: { fontSize: 40, lineHeight: 1.12, letterSpacing: "-0.032em", fontWeight: 600 },
      h2: { fontSize: 34, lineHeight: 1.16, letterSpacing: "-0.028em", fontWeight: 600 },
      h3: { fontSize: 29, lineHeight: 1.2, letterSpacing: "-0.024em", fontWeight: 600 },
      h4: {
        fontSize: "clamp(1.75rem, 2.2vw, 2rem)",
        lineHeight: 1.2,
        letterSpacing: "-0.022em",
        fontWeight: 600,
      },
      h5: { fontSize: 22, lineHeight: 1.28, letterSpacing: "-0.016em", fontWeight: 600 },
      h6: { fontSize: 18, lineHeight: 1.35, letterSpacing: "-0.01em", fontWeight: 600 },
      subtitle1: { fontSize: 16, lineHeight: 1.45, letterSpacing: "-0.006em", fontWeight: 600 },
      subtitle2: { fontSize: 14, lineHeight: 1.45, letterSpacing: 0, fontWeight: 600 },
      body1: { fontSize: 15, lineHeight: 1.65, letterSpacing: 0 },
      body2: { fontSize: 14, lineHeight: 1.55, letterSpacing: "0.002em" },
      caption: { fontSize: 12, lineHeight: 1.45, letterSpacing: "0.012em" },
      overline: {
        fontSize: 11,
        lineHeight: 1.45,
        letterSpacing: "0.075em",
        fontWeight: 600,
        textTransform: "uppercase",
      },
      button: {
        fontSize: 14,
        lineHeight: 1.2,
        textTransform: "none",
        fontWeight: 600,
        letterSpacing: "0.005em",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ":root": {
            colorScheme: mode,
            "--atlas-code-bg": dark ? "#222c26" : "#e8eeea",
            "--atlas-code-text": dark ? "#d9e8df" : "#34423a",
            "--atlas-scrollbar": dark ? "#536159" : "#bac5bf",
            "--atlas-surface-soft": dark
              ? "rgba(21, 29, 24, 0.76)"
              : "rgba(251, 253, 252, 0.72)",
            "--atlas-surface-strong": dark
              ? "rgba(21, 29, 24, 0.9)"
              : "rgba(251, 253, 252, 0.88)",
            "--atlas-surface-border": surfaceBorder,
            "--atlas-surface-highlight": surfaceHighlight,
          },
          body: {
            backgroundColor: background,
            color: textPrimary,
            fontVariantNumeric: "tabular-nums",
            textRendering: "optimizeLegibility",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            backgroundImage: dark
              ? [
                  "radial-gradient(circle at 8% 0%, rgba(83, 91, 145, 0.14), transparent 30rem)",
                  "radial-gradient(circle at 88% 6%, rgba(45, 130, 125, 0.15), transparent 34rem)",
                  "radial-gradient(circle at 78% 94%, rgba(60, 122, 93, 0.11), transparent 32rem)",
                ].join(", ")
              : [
                  "radial-gradient(circle at 7% 0%, rgba(164, 174, 229, 0.28), transparent 31rem)",
                  "radial-gradient(circle at 88% 4%, rgba(127, 194, 211, 0.22), transparent 35rem)",
                  "radial-gradient(circle at 82% 96%, rgba(119, 197, 157, 0.25), transparent 34rem)",
                  "radial-gradient(circle at 38% 78%, rgba(232, 196, 139, 0.1), transparent 25rem)",
                ].join(", "),
            backgroundAttachment: "fixed",
            backgroundRepeat: "no-repeat",
          },
          "::selection": {
            backgroundColor: dark ? "#285a54" : "#cde5df",
            color: textPrimary,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            minHeight: 40,
            borderRadius: 10,
            paddingInline: 16,
            whiteSpace: "nowrap",
            transition: "background-color 140ms ease, border-color 140ms ease, transform 140ms ease",
            "&:active": { transform: "translateY(1px)" },
          },
          outlined: { borderColor: dark ? "#4a5750" : "#c8d1cc" },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            "&:active": { transform: "translateY(1px)" },
          },
        },
      },
      MuiTextField: { defaultProps: { size: "small" } },
      MuiInputBase: {
        styleOverrides: {
          root: { fontSize: 14, lineHeight: 1.5 },
          input: { "&::placeholder": { opacity: 0.74 } },
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { fontSize: 14, fontWeight: 500 } },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { fontSize: 12, lineHeight: 1.45 } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: alpha(paper, dark ? 0.86 : 0.76),
            backdropFilter: "blur(12px) saturate(115%)",
            WebkitBackdropFilter: "blur(12px) saturate(115%)",
            boxShadow: `inset 0 1px 0 ${surfaceHighlight}`,
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: dark ? "#66756c" : "#9da9a2",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: 2 },
            "@media (prefers-reduced-transparency: reduce)": {
              backgroundColor: paper,
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
            },
          },
          notchedOutline: { borderColor: dark ? "#4a5750" : "#c8d1cc" },
        },
      },
      MuiPaper: {
        styleOverrides: {
          outlined: {
            borderColor: alpha(border, 0.9),
            borderRadius: 14,
            backgroundColor: alpha(paper, dark ? 0.88 : 0.78),
            backdropFilter: "blur(14px) saturate(115%)",
            WebkitBackdropFilter: "blur(14px) saturate(115%)",
            boxShadow: `inset 0 1px 0 ${surfaceHighlight}`,
            "@media (prefers-reduced-transparency: reduce)": {
              backgroundColor: paper,
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
            },
          },
          elevation1: {
            border: `1px solid ${surfaceBorder}`,
            borderRadius: 14,
            backgroundColor: alpha(paper, dark ? 0.8 : 0.7),
            backdropFilter: "blur(20px) saturate(118%)",
            WebkitBackdropFilter: "blur(20px) saturate(118%)",
            boxShadow: `inset 0 1px 0 ${surfaceHighlight}, 0 0 0 1px ${alpha(
              border,
              dark ? 0.55 : 0.48,
            )}, ${softShadow}`,
            "@media (prefers-reduced-transparency: reduce)": {
              backgroundColor: paper,
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
            },
          },
        },
      },
      MuiCard: {
        defaultProps: { variant: "outlined" },
        styleOverrides: {
          root: {
            borderColor: alpha(border, 0.9),
            borderRadius: 14,
            backgroundColor: alpha(paper, dark ? 0.88 : 0.78),
            backdropFilter: "blur(14px) saturate(115%)",
            WebkitBackdropFilter: "blur(14px) saturate(115%)",
            boxShadow: `inset 0 1px 0 ${surfaceHighlight}`,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: { root: { borderRadius: 14, overflowX: "auto" } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: 13.5,
            lineHeight: 1.5,
            borderColor: border,
            padding: "13px 18px",
          },
          head: {
            color: textSecondary,
            backgroundColor: dark ? "rgba(29, 38, 31, 0.92)" : "rgba(248, 250, 248, 0.9)",
            fontSize: 11,
            lineHeight: 1.35,
            fontWeight: 700,
            letterSpacing: "0.065em",
            textTransform: "uppercase",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 600 },
          sizeSmall: { height: 26, fontSize: 12 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 12, alignItems: "center", fontSize: 14, lineHeight: 1.5 },
          standardSuccess: { backgroundColor: dark ? "#1c3325" : "#edf6ef" },
          standardWarning: { backgroundColor: dark ? "#38291a" : "#fff5e8" },
          standardError: { backgroundColor: dark ? "#3a2023" : "#fbeff0" },
          standardInfo: { backgroundColor: dark ? "#1d3035" : "#eef4f5" },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            border: `1px solid ${surfaceBorder}`,
            borderRadius: 18,
            backgroundColor: alpha(paper, dark ? 0.94 : 0.9),
            backdropFilter: "blur(24px) saturate(118%)",
            WebkitBackdropFilter: "blur(24px) saturate(118%)",
            boxShadow: dark
              ? "0 24px 70px rgba(0, 0, 0, 0.45)"
              : "0 24px 70px rgba(32, 47, 39, 0.18)",
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: { root: { borderRadius: 10 } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { borderRadius: 8, fontSize: 12, lineHeight: 1.4, letterSpacing: "0.01em" },
        },
      },
    },
  });
}

export const theme = createAtlasTheme("light");
